import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import api from "services/api";

const statusLabel = {
  DRAFT: "Draft",
  DIAJUKAN: "Diajukan",
  VERIFIKASI: "Diverifikasi",
  PERBAIKAN: "Perlu Perbaikan",
  PROSES_TEKNIS: "Proses Teknis",
  DISETUJUI: "Disetujui",
  DITOLAK: "Ditolak",
  PENANDATANGANAN: "Penandatanganan",
  SELESAI: "Selesai",
};

function PengajuanRiwayatDialog({ open, pengajuanId, onClose, onOpenDetail }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !pengajuanId) return;
    setLoading(true);
    api
      .get(`pengajuan/${pengajuanId}`)
      .then((res) => setData(res.data?.data))
      .catch(() => alert("Gagal memuat riwayat pengajuan"))
      .finally(() => setLoading(false));
  }, [open, pengajuanId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        Riwayat Pengajuan
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <MDTypography>Memuat riwayat...</MDTypography>
        ) : !data ? (
          <MDTypography color="text">Data tidak ditemukan.</MDTypography>
        ) : (
          <>
            <MDBox mb={2}>
              <MDTypography variant="h6" fontWeight="medium">
                {data.nomor_pengajuan}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                {data.nama_jenis_izin} • {data.nama_pemohon}
              </MDTypography>
              <MDBox mt={1}>
                <Chip label={statusLabel[data.status] || data.status} size="small" color="info" />
              </MDBox>
            </MDBox>

            <MDTypography variant="button" fontWeight="medium" mb={1}>
              Riwayat Proses ({(data.riwayat || []).length})
            </MDTypography>

            {(data.riwayat || []).length === 0 ? (
              <MDTypography variant="body2" color="text">
                Belum ada riwayat proses.
              </MDTypography>
            ) : (
              <List dense disablePadding>
                {data.riwayat.map((r, idx) => (
                  <ListItem key={r.id} alignItems="flex-start" sx={{ px: 0 }}>
                    <MDBox
                      minWidth="1.5rem"
                      height="1.5rem"
                      borderRadius="50%"
                      bgColor="info"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={1.5}
                      mt={0.25}
                      fontSize="0.7rem"
                    >
                      {idx + 1}
                    </MDBox>
                    <ListItemText
                      primary={`${statusLabel[r.status] || r.status}${r.keterangan ? ` — ${r.keterangan}` : ""}`}
                      secondary={`${new Date(r.created_at).toLocaleString("id-ID")}${r.nama_user ? ` • ${r.nama_user}` : ""}`}
                      primaryTypographyProps={{ variant: "body2" }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>
          Tutup
        </MDButton>
        {data?.id ? (
          <MDButton variant="gradient" color="info" onClick={() => onOpenDetail(data.id)}>
            Buka Detail Lengkap
          </MDButton>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

PengajuanRiwayatDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  pengajuanId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onOpenDetail: PropTypes.func.isRequired,
};

export default PengajuanRiwayatDialog;
